import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/utils/helpers"

const labelVariants = cva("block", {
  variants: {
    variant: {
      default: "",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    size: {
      default: "",
      xs: "",
      sm: "",
      md: "",
      lg: "",
      xl: "text-xl",
      "2xl": "",
      icon: "h-9 w-9",
    },
    type: {
      default: "",
      hyperlink: "cursor-pointer underline",
      upperCase: "uppercase",
    },
    family: {
      default: "font-inter",
      roboto: "font-roboto-mono",
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      type: "default",
      family: "default",
    },
  },
})

function labelClassName({
  size = "default",
  variant = "default",
  family = "default",
}: VariantProps<typeof labelVariants>): string {
  if (size === "default" && variant === "default") return ""
  const className = `text-${size === "default" ? "md" : size}${variant === "default" ? "" : "-" + variant}${family === "roboto" ? "-mono" : ""}`
  return className
}

export interface LabelProps
  extends React.HTMLAttributes<HTMLLabelElement>,
    VariantProps<typeof labelVariants> {
  asChild?: boolean
}

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  (
    { className, variant, size, type, family, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "label"
    return (
      <Comp
        className={cn(
          labelVariants({ variant, size, type, family }),
          labelClassName({ variant, size, family }),
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  },
)
Label.displayName = "Label"

export { Label, labelVariants }
